import {
    useState,
    useEffect,
    useMemo,
    type CSSProperties,
    type FC,
    type ReactNode,
} from 'react'
import {
    Stack,
    Group,
    Paper,
    Text,
    Title,
    Card,
    SimpleGrid,
    Badge,
    Button,
    Slider,
    Select,
    Switch,
    Progress,
    Loader,
    Overlay,
    Divider,
    Alert,
    Box,
} from '@mantine/core'
import {
    IconChartArrows,
    IconInfoCircle,
    IconTargetArrow,
} from '@tabler/icons-react'
import { genericMemo } from '../../../components/genericMemo'
import { AttentionBanner } from '@/components/AttentionBanner'
import { datasetUniverseSize } from '@/lib/datasetNames'
import { colorForDataset } from '@/lib/datasetColors'
import { DatasetType } from '@/lib/domain'
import type { AudienceConfiguration, Dataset } from '@/lib/domain'
import { formatCompactNumber } from '@/lib/formatNumber'
import { MODEL_MULTIPLIERS, similarityShares, propensityDeciles } from '../api'
import { MODELING_PRIORITY_LABEL } from '../labels'
import type { ModelingPriority } from '../types'

// The two ways we grow a seed segment into a deliverable audience.
export type ModelKind = 'propensity' | 'expansion'

const MODEL_KINDS: {
    value: ModelKind
    label: string
    description: string
    icon: typeof IconTargetArrow
}[] = [
    {
        value: 'propensity',
        label: 'Propensity model',
        icon: IconTargetArrow,
        description:
            'Stays within this segment’s own datasets and adds users who are likely to do the behavior but haven’t yet — including those who did it where we lacked direct visibility.',
    },
    {
        value: 'expansion',
        label: 'Expansion model (lookalike)',
        icon: IconChartArrows,
        description:
            'Uses this segment as a seed and finds similar users in a separate user-attribute dataset, modeling against that dataset’s attributes.',
    },
]

// Labels come from MODELING_PRIORITY_LABEL (shared); only the card descriptions
// live here.
const PRIORITY_OPTIONS: {
    value: ModelingPriority
    description: string
}[] = [
    { value: 'balanced', description: 'Balance between reach and similarity' },
    {
        value: 'similarity',
        description: 'Prioritize finding the most similar users',
    },
    { value: 'reach', description: 'Maximize the number of potential users' },
]

// Selectable card chrome — brand-tinted when active, matching the cart/makeup
// surfaces (var(--mantine-primary-color-*)) rather than a hardcoded blue.
const selectableCardStyle = (
    isSelected: boolean,
    locked: boolean
): CSSProperties => ({
    cursor: locked ? 'default' : 'pointer',
    borderColor: isSelected ? 'var(--mantine-primary-color-filled)' : undefined,
    borderWidth: isSelected ? 2 : 1,
    backgroundColor: isSelected
        ? 'var(--mantine-primary-color-light)'
        : undefined,
})

// Small uppercase-dimmed field label, matching the "DATA SOURCE" / "REACH"
// labels on the makeup and cart surfaces.
const FieldLabel: FC<{ children: ReactNode }> = ({ children }) => (
    <Text size='xs' c='dimmed' tt='uppercase' fw={600} mb='xs'>
        {children}
    </Text>
)

// Post-run model quality. The numbers are derived from the chosen priority via
// the shared similarityShares / propensityDeciles helpers (stub — no real
// scoring yet, but always in sync with the reach multiplier). Lookalike: how the
// modeled audience distributes across similarity bands. Propensity: predicted
// conversion rate per decile and the lift of the top decile over the average.
const SIMILARITY_BANDS = ['90–100%', '80–90%', '70–80%', '60–70%']
const DECILE_LABELS = Array.from({ length: 10 }, (_, i) => `decile-${i + 1}`)

const ModelQuality: FC<{
    modelType: ModelKind
    priority: ModelingPriority
}> = ({ modelType, priority }) => {
    if (modelType === 'expansion') {
        const shares = similarityShares(priority)
        const avg = Math.round(
            shares.reduce((sum, s, i) => sum + s * (95 - i * 10), 0) / 100
        )
        return (
            <div>
                <FieldLabel>Similarity distribution</FieldLabel>
                <Stack gap={8}>
                    {SIMILARITY_BANDS.map((band, i) => (
                        <Group key={band} gap='sm' wrap='nowrap'>
                            <Text size='xs' c='dimmed' w={64} ta='right'>
                                {band}
                            </Text>
                            <Progress
                                value={shares[i]}
                                color='var(--mantine-primary-color-filled)'
                                style={{ flex: 1 }}
                                size='lg'
                                radius='sm'
                            />
                            <Text size='xs' w={32}>
                                {shares[i]}%
                            </Text>
                        </Group>
                    ))}
                </Stack>
                <Text size='xs' c='dimmed' mt='xs'>
                    Average similarity to seed ~{avg}%
                </Text>
            </div>
        )
    }
    const deciles = propensityDeciles(priority)
    const avg = deciles.reduce((a, b) => a + b, 0) / deciles.length
    const lift = (deciles[0] / avg).toFixed(1)
    const max = deciles[0]
    return (
        <div>
            <FieldLabel>Propensity by decile</FieldLabel>
            <Group gap={4} align='flex-end' h={80}>
                {DECILE_LABELS.map((label, i) => (
                    <Box
                        key={label}
                        style={{
                            flex: 1,
                            height: `${(deciles[i] / max) * 100}%`,
                            background: 'var(--mantine-primary-color-filled)',
                            borderRadius: 2,
                        }}
                    />
                ))}
            </Group>
            <Group justify='space-between' mt={4}>
                <Text size='xs' c='dimmed'>
                    Most likely
                </Text>
                <Text size='xs' c='dimmed'>
                    Least likely
                </Text>
            </Group>
            <Text size='sm' mt='xs'>
                Top decile converts{' '}
                <Text span fw={700} c='var(--mantine-primary-color-filled)'>
                    {lift}×
                </Text>{' '}
                the audience average.
            </Text>
        </div>
    )
}

export interface ModelConfig {
    modelType: ModelKind
    selectedModel: ModelingPriority
    lookalikePercentage: number
    // Set only for expansion models — the user-attribute dataset to model into.
    expansionDatasetId?: string
    // Whether the original seed members are dropped from the delivered audience.
    excludeSeed: boolean
    // The delivered reach the user saw when creating the model (so the stored
    // lifecycle value matches the screen).
    expandedSize: number
}

export interface ModelingOptionsProps {
    seedAudience: AudienceConfiguration
    audienceSize: number | null
    // The thread's available datasets; the user-attribute ones are the expansion
    // (lookalike) targets.
    datasets: Dataset[]
    // Dataset id → display name, for the seed/pool dataset labels.
    names: Record<string, string>
    onCancel: () => void
    onCreateModel: (config: ModelConfig) => void
}

export const ModelingOptions: FC<ModelingOptionsProps> = genericMemo(
    ({
        seedAudience,
        audienceSize,
        datasets,
        names,
        onCancel,
        onCreateModel,
    }) => {
        // Expansion targets: every user-attribute dataset in the thread. Modeling
        // into the seed's own dataset (a within-dataset lookalike) is allowed but
        // warned about below.
        const expansionDatasets = useMemo(
            () =>
                datasets.filter(
                    (d) => d.dataset_type === DatasetType.USER_ATTRIBUTE
                ),
            [datasets]
        )

        // The dataset a user-attribute seed is itself built on. Used to warn when
        // the expansion target is that same dataset. Only user-attribute seeds
        // can collide this way — a crossed seed's transaction behavior makes the
        // expansion meaningfully different, so it's never flagged.
        const seedAttributeDatasetId =
            seedAudience.dataset_type === DatasetType.USER_ATTRIBUTE
                ? seedAudience.dataset_id
                : undefined

        const [modelType, setModelType] = useState<ModelKind>('propensity')
        const [selectedModel, setSelectedModel] =
            useState<ModelingPriority>('balanced')
        const [lookalikePercentage, setLookalikePercentage] =
            useState<number>(5)
        // The user's explicit pick (null until they choose). The effective value
        // below falls back to the sole candidate, so a single dataset is
        // defaulted without writing state in an effect.
        const [pickedExpansionDatasetId, setPickedExpansionDatasetId] =
            useState<string | null>(null)
        const [excludeSeed, setExcludeSeed] = useState<boolean>(true)
        const [modelingState, setModelingState] = useState<
            'idle' | 'running' | 'completed'
        >('idle')
        const [hasRunModeling, setHasRunModeling] = useState<boolean>(false)

        const baseAudienceSize = audienceSize ?? 0

        // Default the expansion dataset when exactly one candidate exists.
        const expansionDatasetId =
            pickedExpansionDatasetId ??
            (expansionDatasets.length === 1 ? expansionDatasets[0].id : null)

        // 5-second modeling simulation (mock; see localStorage stub).
        useEffect(() => {
            if (modelingState === 'running') {
                const timer = setTimeout(() => {
                    setModelingState('completed')
                    setHasRunModeling(true)
                }, 5000)
                return () => clearTimeout(timer)
            }
        }, [modelingState])

        const needsExpansionDataset = modelType === 'expansion'
        const missingExpansionDataset =
            needsExpansionDataset && !expansionDatasetId
        const noExpansionTargets =
            needsExpansionDataset && expansionDatasets.length === 0
        // Within-dataset lookalike: modeling into the same dataset the seed is
        // built on. Allowed, but worth flagging.
        const sameAsSeedDataset =
            needsExpansionDataset &&
            expansionDatasetId != null &&
            expansionDatasetId === seedAttributeDatasetId

        const datasetName = (id: string): string => names[id] ?? id

        // The dataset(s) the seed is drawn from, shown as context chips.
        const seedDatasetIds =
            seedAudience.dataset_type === DatasetType.CROSSED
                ? [
                      seedAudience.dataset_id,
                      seedAudience.users_with_attributes_dataset_id,
                  ]
                : [seedAudience.dataset_id]

        // The pool the model runs against: the target dataset for an expansion
        // (lookalike), or the seed's own source dataset for a propensity model.
        const poolDatasetId =
            modelType === 'expansion'
                ? expansionDatasetId
                : seedAudience.dataset_id
        const poolUniverse =
            poolDatasetId != null
                ? datasetUniverseSize[poolDatasetId]
                : undefined

        // Modeled footprint and what we'd actually deliver. Gross is scaled off
        // the pool universe (falling back to the seed when the universe is
        // unknown); net-new strips the overlap with the seed; excludeSeed decides
        // which one ships. (Mock math — stub.)
        const multiplier = MODEL_MULTIPLIERS[selectedModel]
        const grossReach =
            poolUniverse != null
                ? Math.round(
                      poolUniverse * (lookalikePercentage / 100) * multiplier
                  )
                : Math.round(
                      baseAudienceSize * lookalikePercentage * multiplier
                  )
        const overlap = Math.min(baseAudienceSize, grossReach)
        const netNew = Math.max(0, grossReach - overlap)
        const deliveredReach = excludeSeed ? netNew : grossReach
        const pctOfPool =
            poolUniverse != null && poolUniverse > 0
                ? (deliveredReach / poolUniverse) * 100
                : null

        // Seed-size quality (per lookalike best practice: <100 too small to be
        // reliable; a few thousand+ is healthy). null = no caveat.
        const seedQuality =
            baseAudienceSize > 0 && baseAudienceSize < 100
                ? {
                      color: 'error',
                      text: 'Seed is very small — modeling may be unreliable.',
                  }
                : baseAudienceSize < 1000
                  ? {
                        color: 'warning',
                        text: 'Small seed — a larger audience models more reliably.',
                    }
                  : null

        const reset = () => {
            setModelingState('idle')
            setHasRunModeling(false)
        }

        return (
            <Stack gap='lg' pos='relative'>
                <div
                    style={{
                        opacity: modelingState === 'running' ? 0.5 : 1,
                    }}
                >
                    {/* Seed context: the dataset(s) the seed is drawn from, plus
                        a caveat when the seed is small (less reliable to model). */}
                    <Stack gap='xs' mb='md'>
                        <Group gap='xs' align='center'>
                            <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                                Seed audience
                            </Text>
                            {seedDatasetIds.map((id) => (
                                <Badge
                                    key={id}
                                    variant='light'
                                    color={colorForDataset(id)}
                                    size='sm'
                                >
                                    {datasetName(id)}
                                </Badge>
                            ))}
                        </Group>
                        {seedQuality && (
                            <Text size='xs' c={seedQuality.color} fw={500}>
                                {seedQuality.text}
                            </Text>
                        )}
                    </Stack>

                    {/* Model type */}
                    <FieldLabel>Model type</FieldLabel>
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='sm'>
                        {MODEL_KINDS.map((kind) => {
                            const isSelected = modelType === kind.value
                            const Icon = kind.icon
                            return (
                                <Card
                                    key={kind.value}
                                    withBorder
                                    radius='md'
                                    p='md'
                                    style={selectableCardStyle(
                                        isSelected,
                                        modelingState === 'running'
                                    )}
                                    onClick={() => {
                                        if (modelingState === 'running') return
                                        setModelType(kind.value)
                                        if (modelingState === 'completed')
                                            reset()
                                    }}
                                >
                                    <Group gap='xs' mb='xs'>
                                        <Icon
                                            size={18}
                                            color='var(--mantine-primary-color-filled)'
                                        />
                                        <Text size='sm' fw={600}>
                                            {kind.label}
                                        </Text>
                                    </Group>
                                    <Text size='xs' c='dimmed'>
                                        {kind.description}
                                    </Text>
                                </Card>
                            )
                        })}
                    </SimpleGrid>

                    {/* Propensity runs within the seed's own dataset(s). */}
                    {!needsExpansionDataset && poolUniverse != null && (
                        <Text size='sm' c='dimmed' mt='md'>
                            Modeling within{' '}
                            <Text span fw={600} c='var(--mantine-color-text)'>
                                {datasetName(seedAudience.dataset_id)}
                            </Text>{' '}
                            — ~{formatCompactNumber(poolUniverse)} reachable
                            users
                        </Text>
                    )}

                    {/* Expansion dataset selector */}
                    {needsExpansionDataset &&
                        (noExpansionTargets ? (
                            <Alert
                                mt='md'
                                color='gray'
                                icon={<IconInfoCircle size={18} />}
                                title='No dataset to expand into'
                            >
                                Expansion models the seed into a user-attribute
                                dataset, and this thread has none available. Run
                                a propensity model instead.
                            </Alert>
                        ) : (
                            <Box mt='md'>
                                <Select
                                    label='Model into dataset'
                                    description={
                                        poolUniverse != null
                                            ? `Searching ~${formatCompactNumber(poolUniverse)} reachable users for similar profiles.`
                                            : 'The user-attribute dataset to find similar users in.'
                                    }
                                    placeholder='Select a dataset'
                                    data={expansionDatasets.map((d) => ({
                                        value: d.id,
                                        label: datasetName(d.id),
                                    }))}
                                    value={expansionDatasetId}
                                    onChange={(value) => {
                                        setPickedExpansionDatasetId(value)
                                        if (modelingState === 'completed')
                                            reset()
                                    }}
                                    disabled={modelingState === 'running'}
                                    allowDeselect={false}
                                />
                                {sameAsSeedDataset && (
                                    <AttentionBanner
                                        mt='sm'
                                        title='Modeling within the same dataset'
                                    >
                                        This expands the seed into the same
                                        dataset it’s built on. Expect heavy
                                        overlap with the original audience —
                                        choose a different dataset for a true
                                        lookalike.
                                    </AttentionBanner>
                                )}
                            </Box>
                        ))}

                    {/* Modeling priority */}
                    <Box mt='lg'>
                        <FieldLabel>Modeling priority</FieldLabel>
                    </Box>
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing='sm'>
                        {PRIORITY_OPTIONS.map((option) => {
                            const isSelected = selectedModel === option.value
                            return (
                                <Card
                                    key={option.value}
                                    withBorder
                                    radius='md'
                                    p='md'
                                    style={selectableCardStyle(
                                        isSelected,
                                        modelingState === 'running'
                                    )}
                                    onClick={() => {
                                        if (modelingState === 'running') return
                                        setSelectedModel(option.value)
                                        if (modelingState === 'completed')
                                            reset()
                                    }}
                                >
                                    <Text size='sm' fw={600} mb='xs'>
                                        {MODELING_PRIORITY_LABEL[option.value]}
                                    </Text>
                                    <Text size='xs' c='dimmed'>
                                        {option.description}
                                    </Text>
                                </Card>
                            )
                        })}
                    </SimpleGrid>

                    <Group
                        justify='flex-end'
                        mt='md'
                        style={{
                            visibility:
                                modelingState === 'idle' ? 'visible' : 'hidden',
                        }}
                    >
                        <Button
                            onClick={() => setModelingState('running')}
                            variant='filled'
                            disabled={missingExpansionDataset}
                        >
                            Run Modeling
                        </Button>
                    </Group>
                </div>

                {/* Results — shown after modeling has run */}
                {hasRunModeling && (
                    <div
                        style={{
                            opacity: modelingState === 'running' ? 0.5 : 1,
                        }}
                    >
                        <Divider mb='lg' />
                        <Title order={5}>
                            {modelType === 'expansion'
                                ? 'Audience expansion'
                                : 'Propensity expansion'}
                        </Title>
                        <Text size='sm' c='dimmed' mb='md'>
                            {modelType === 'expansion'
                                ? 'Find new users similar to your current segment'
                                : 'Add users likely to do the behavior'}
                        </Text>

                        <Group gap='xl' align='flex-start' wrap='nowrap'>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <FieldLabel>
                                    {modelType === 'expansion'
                                        ? 'Similarity level'
                                        : 'Propensity level'}
                                </FieldLabel>
                                <Slider
                                    color='var(--mantine-primary-color-filled)'
                                    value={lookalikePercentage}
                                    onChange={setLookalikePercentage}
                                    min={1}
                                    max={10}
                                    step={1}
                                    marks={[
                                        { value: 1, label: '1%' },
                                        { value: 5, label: '5%' },
                                        { value: 10, label: '10%' },
                                    ]}
                                    label={(value) => `${value}%`}
                                    style={{
                                        marginTop: '0.5rem',
                                        marginBottom: '2rem',
                                    }}
                                />
                                <Text size='xs' c='dimmed' mt='xs'>
                                    Lower percentage = more similar users
                                </Text>
                            </div>

                            <Paper p='lg' withBorder radius='md'>
                                <Stack gap={4} align='flex-end'>
                                    <Text
                                        fz={32}
                                        fw={700}
                                        lh={1.1}
                                        c='var(--mantine-primary-color-filled)'
                                    >
                                        ~{formatCompactNumber(deliveredReach)}
                                    </Text>
                                    <Text
                                        size='xs'
                                        c='dimmed'
                                        tt='uppercase'
                                        fw={600}
                                    >
                                        Estimated reach
                                    </Text>
                                    {pctOfPool != null && (
                                        <Badge
                                            variant='light'
                                            color='var(--mantine-primary-color-filled)'
                                            mt={4}
                                        >
                                            {pctOfPool < 10
                                                ? pctOfPool.toFixed(1)
                                                : Math.round(pctOfPool)}
                                            % of pool
                                        </Badge>
                                    )}
                                    <Text size='xs' c='dimmed' ta='right'>
                                        {excludeSeed
                                            ? `excludes ~${formatCompactNumber(overlap)} seed members`
                                            : `incl. ~${formatCompactNumber(netNew)} net-new`}
                                    </Text>
                                </Stack>
                            </Paper>
                        </Group>

                        <Switch
                            mt='lg'
                            checked={excludeSeed}
                            onChange={(event) =>
                                setExcludeSeed(event.currentTarget.checked)
                            }
                            label='Exclude the seed audience from results'
                            description='Deliver only net-new users, not people already in the seed.'
                        />

                        <Divider my='lg' />
                        <ModelQuality
                            modelType={modelType}
                            priority={selectedModel}
                        />

                        <Group justify='flex-end' mt='xl'>
                            <Button variant='default' onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                disabled={missingExpansionDataset}
                                onClick={() =>
                                    onCreateModel({
                                        modelType,
                                        selectedModel,
                                        lookalikePercentage,
                                        expansionDatasetId:
                                            modelType === 'expansion'
                                                ? (expansionDatasetId ??
                                                  undefined)
                                                : undefined,
                                        excludeSeed,
                                        expandedSize: deliveredReach,
                                    })
                                }
                            >
                                Create{' '}
                                {modelType === 'expansion'
                                    ? 'Audience Expansion'
                                    : 'Propensity Audience'}
                            </Button>
                        </Group>
                    </div>
                )}

                {/* Running overlay */}
                {modelingState === 'running' && (
                    <Overlay
                        color='var(--mantine-color-body)'
                        backgroundOpacity={0.75}
                        blur={2}
                        radius='md'
                        center
                        zIndex={5}
                    >
                        <Stack align='center' gap='md'>
                            <Loader size='xl' />
                            <Text size='lg' fw={600}>
                                Running modeling…
                            </Text>
                            <Text size='sm' c='dimmed'>
                                This may take up to 3 hours
                            </Text>
                            <Button
                                variant='default'
                                onClick={() => setModelingState('idle')}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    </Overlay>
                )}
            </Stack>
        )
    }
)
